import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { getRouteApi } from '@tanstack/react-router';
import { LayoutGrid, List, Plus, SearchIcon, SlidersHorizontal, XIcon } from 'lucide-react';

import { badgeVariants } from '@/components/new-ui/badge';
import { Button } from '@/components/new-ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/new-ui/dropdown-menu';
import { Input } from '@/components/new-ui/input';
import {
  countActiveFilters,
  QuickFilterChip,
} from '@/features/estates/components/estates-filter-shared';
import { ESTATES_TOOLBAR_ROUTES } from '@/features/estates/constants/routes';
import { useDebouncedCallback } from '@/hooks/use-debounced-callback';
import { cn } from '@/lib/utils';

const route = getRouteApi(ESTATES_TOOLBAR_ROUTES.PROJECT_OBJECTS);

type ProjectObjectsFilters = {
  searchString?: string;
  propertyType?: string;
  marketType?: string;
  usageType?: string;
};

type ViewMode = 'list' | 'grid';

const VIEW_ICONS: Record<ViewMode, typeof List> = { list: List, grid: LayoutGrid };
const VIEW_ORDER: ViewMode[] = ['grid', 'list'];

type ProjectObjectsToolbarProps = {
  /** Shown only for users who can write the project, opens the create estate modal. */
  onAddEstate?: () => void;
};

/**
 * Search, filters, view menu, new estate button and quick filter chips of the project estates
 * tab. Everything is stored in the route search params, like the estates page does.
 */
export const ProjectObjectsToolbar = ({ onAddEstate }: ProjectObjectsToolbarProps) => {
  const { t } = useTranslation();
  const search = route.useSearch();
  const navigate = route.useNavigate();

  const filters = useMemo(() => (search.filters ?? {}) as ProjectObjectsFilters, [search.filters]);
  const view = (search.view || 'list') as ViewMode;
  const CurrentViewIcon = VIEW_ICONS[view];
  const otherViews = VIEW_ORDER.filter((mode) => mode !== view);
  const NextViewIcon = VIEW_ICONS[otherViews[0] ?? view];
  const setView = (mode: ViewMode) => {
    navigate({ search: (prev) => ({ ...prev, view: mode }), replace: true });
  };
  const enableFilters = Boolean((search as { enableFilters?: boolean }).enableFilters);
  const activeFiltersCount = countActiveFilters(search.filters);

  const urlSearch =
    typeof filters.searchString === 'string' || typeof filters.searchString === 'number'
      ? String(filters.searchString)
      : '';
  const [inputValue, setInputValue] = useState(urlSearch);
  // The search this toolbar wrote to the URL last. A different URL value came from outside
  // (browser back/forward, Clear All Filters), and only then the input follows the URL
  const syncedSearchRef = useRef(urlSearch);

  const updateFilters = useCallback(
    (updater: (filters: ProjectObjectsFilters) => ProjectObjectsFilters) => {
      navigate({
        search: (prev) => {
          const prevFilters = (prev.filters ?? {}) as ProjectObjectsFilters;
          const nextFilters = updater({ ...prevFilters });

          const prevKeys = Object.keys(prevFilters);
          const nextKeys = Object.keys(nextFilters);
          const hasSameValues =
            prevKeys.length === nextKeys.length &&
            prevKeys.every(
              (key) =>
                prevFilters[key as keyof ProjectObjectsFilters] ===
                nextFilters[key as keyof ProjectObjectsFilters]
            );

          if (hasSameValues) return prev;

          return { ...prev, filters: nextFilters };
        },
      });
    },
    [navigate]
  );

  const debouncedUpdateSearch = useDebouncedCallback((value: string) => {
    syncedSearchRef.current = value.trim().length ? value : '';
    updateFilters((next) => {
      if (value.trim().length) {
        next.searchString = value;
      } else {
        delete next.searchString;
      }
      return next;
    });
  }, 1000);

  useEffect(() => {
    if (urlSearch === syncedSearchRef.current) return;
    syncedSearchRef.current = urlSearch;
    // A pending search would write the stale input back over the new URL value
    debouncedUpdateSearch.cancel();
    setInputValue(urlSearch);
  }, [urlSearch, debouncedUpdateSearch]);

  const handleClearSearch = () => {
    // Drop a pending debounced search, otherwise it restores the old searchString after the clear
    debouncedUpdateSearch.cancel();
    syncedSearchRef.current = '';
    setInputValue('');
    updateFilters((next) => {
      delete next.searchString;
      return next;
    });
  };

  const toggleFilter = (key: 'propertyType' | 'marketType' | 'usageType', value: string) => {
    updateFilters((next) => {
      if (next[key] === value) {
        delete next[key];
      } else {
        next[key] = value;
      }
      return next;
    });
  };

  const toggleDrawer = () => {
    navigate({
      search: (prev) => ({
        ...prev,
        enableFilters: 'enableFilters' in prev ? !prev.enableFilters : true,
      }),
      replace: true,
    });
  };

  const chips: {
    key: 'propertyType' | 'marketType' | 'usageType';
    value: string;
    label: string;
  }[] = [
    { key: 'propertyType', value: 'house', label: t('house') },
    { key: 'propertyType', value: 'apartment', label: t('apartment') },
    { key: 'usageType', value: 'commercial', label: t('commercial') },
    { key: 'marketType', value: 'buy', label: t('purchase') },
    { key: 'marketType', value: 'rent', label: t('rent') },
  ];

  return (
    <div className="new-ui flex flex-col gap-6">
      <div className="flex flex-wrap gap-3">
        <div className="relative min-w-0 flex-[1_1_240px]">
          <SearchIcon className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            className={cn('pl-9', inputValue && 'pr-9')}
            placeholder={t('searchEstates')}
            aria-label={t('searchEstates')}
            value={inputValue}
            onChange={(e) => {
              const value = e.target.value;
              setInputValue(value);
              debouncedUpdateSearch(value);
            }}
          />
          {inputValue ? (
            <button
              type="button"
              onClick={handleClearSearch}
              aria-label={t('clearSearch')}
              className="absolute right-2 top-1/2 flex h-6 w-6 -translate-y-1/2 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-accent hover:text-accent-foreground"
            >
              <XIcon className="h-4 w-4" />
            </button>
          ) : null}
        </div>
        <Button
          type="button"
          variant="outline"
          className="relative gap-2"
          aria-label={t('filter')}
          aria-pressed={enableFilters}
          onClick={toggleDrawer}
        >
          <SlidersHorizontal className="h-4 w-4" />
          <span>{t('filter')}</span>
          {/* span, not the Badge div, because a div is not allowed inside a button */}
          {activeFiltersCount > 0 ? (
            <span className={cn(badgeVariants({ variant: 'secondary' }), 'ml-1 h-5 min-w-5 px-1')}>
              {activeFiltersCount}
            </span>
          ) : null}
        </Button>
        {otherViews.length === 1 ? (
          // Only one other view: a plain toggle, the menu returns once a third view (map) exists
          <Button
            type="button"
            variant="outline"
            className="gap-2"
            aria-label={`${t('view')}: ${t(otherViews[0])}`}
            onClick={() => setView(otherViews[0])}
          >
            <NextViewIcon className="h-4 w-4" />
            <span>{t(otherViews[0])}</span>
          </Button>
        ) : (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button type="button" variant="outline" className="gap-2" aria-label={t('view')}>
                <CurrentViewIcon className="h-4 w-4" />
                <span>{t(view)}</span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="end"
              className="z-50 border border-border bg-background shadow-lg"
            >
              {otherViews.map((mode) => {
                const Icon = VIEW_ICONS[mode];
                return (
                  <DropdownMenuItem
                    key={mode}
                    className="cursor-pointer gap-2"
                    onSelect={() => setView(mode)}
                  >
                    <Icon className="h-4 w-4" /> {t(mode)}
                  </DropdownMenuItem>
                );
              })}
            </DropdownMenuContent>
          </DropdownMenu>
        )}
        {onAddEstate ? (
          <Button type="button" className="gap-2" onClick={onAddEstate}>
            <Plus className="h-4 w-4" />
            {t('newEstate')}
          </Button>
        ) : null}
      </div>

      <div className="flex flex-wrap items-center gap-2">
        {chips.map((chip) => {
          const active = filters[chip.key] === chip.value;
          return (
            <QuickFilterChip
              key={`${chip.key}-${chip.value}`}
              active={active}
              onClick={() => toggleFilter(chip.key, chip.value)}
            >
              {chip.label}
            </QuickFilterChip>
          );
        })}
      </div>
    </div>
  );
};
